import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import interactiveRegistry from "../../Registry/Interactive/InteractiveRegistry";
import "./ObjectsDropdown.css";
import Icon from "./Icon";
import PropTypes from "prop-types";
import { TOOL_ICON_SIZE, BTN_HEIGHT } from "./sidebarConstants";
import { getScopedItem, setScopedItem } from "../../store/workspaceStore";
import useHostDocument from "~/hooks/useHostDocument";
import {
  getObjectsViewModeKey,
  getObjectsSelectedCategoryKey,
} from "../../workspace/constants";

const ObjectsDropdown = ({
  onAddFavorite,
  favorites,
  interactiveApi = null,
  sidebarViewMode = "grid",
  sidebarWidth = 36,
}) => {
  const objectListRef = useRef(null);
  // PoC #popout: the selector dialog must open in the window this panel lives in (#375)
  const [hostDoc, hostProbe] = useHostDocument();
  // Initialize selectedCategory with scoped localStorage value or first category
  const getInitialCategory = () => {
    const savedCategory = getScopedItem(getObjectsSelectedCategoryKey);
    const categories = Object.keys(interactiveRegistry);

    if (savedCategory && categories.includes(savedCategory)) {
      return savedCategory;
    }

    // Auto-select first category if none was saved or saved category doesn't exist
    return categories.length > 0 ? categories[0] : null;
  };

  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => getScopedItem(getObjectsViewModeKey) || "list",
  );

  // Save selected category to scoped localStorage whenever it changes
  useEffect(() => {
    if (selectedCategory) {
      setScopedItem(getObjectsSelectedCategoryKey, selectedCategory);
    }
  }, [selectedCategory]);

  // Save view mode to scoped localStorage whenever it changes
  useEffect(() => {
    setScopedItem(getObjectsViewModeKey, viewMode);
  }, [viewMode]);

  // Handler for category selection - always set the category (no toggle off)
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  // Adicionar/remover favorito
  const handleFavoriteClick = (object) => {
    onAddFavorite(object);
  };

  // Toggle modal
  const toggleModal = () => {
    setModalVisible((prevState) => !prevState);
  };

  // interactive API (optional): registeredTypes, activeInteractive, setActiveInteractive
  const api = interactiveApi;
  const registeredTypes = api ? api.registeredTypes : [];
  const activeInteractive = api ? api.activeInteractive : null;
  const setActiveInteractive = api ? api.setActiveInteractive : () => {};

  // Helper to check if interactive is a favorite (favorites is array of interactive keys)
  const isFavorite = (interactiveKey) => favorites.includes(interactiveKey);

  // Filtro de pesquisa
  // Build searchable flattened list from interactiveRegistry
  const allInteractives = Object.entries(interactiveRegistry).flatMap(
    ([categoryKey, cat]) =>
      Object.entries(cat.interactives).map(([typeKey, entry]) => ({
        category: categoryKey,
        categoryDisplayName: cat.displayName,
        key: entry.key || typeKey,
        type: typeKey,
        displayName: entry.displayName || typeKey,
      })),
  );

  const filteredObjects = allInteractives.filter((it) =>
    searchTerm
      ? it.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      : true,
  );

  // Check if object list is scrollable and add visual indicator
  const checkScrollable = useCallback(() => {
    const container = objectListRef.current?.parentElement; // .objects-indicator-dropdown
    const list = objectListRef.current; // ul element

    if (container && list) {
      const isScrollable = list.scrollHeight > list.clientHeight;
      if (isScrollable) {
        container.classList.add("has-scroll");
      } else {
        container.classList.remove("has-scroll");
        container.classList.remove("scrolled-to-bottom");
      }
    }
  }, []);

  const checkScrollPosition = useCallback(() => {
    const container = objectListRef.current?.parentElement;
    const list = objectListRef.current;

    if (container && list) {
      const isAtBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight < 10;
      if (isAtBottom) {
        container.classList.add("scrolled-to-bottom");
      } else {
        container.classList.remove("scrolled-to-bottom");
      }
    }
  }, []);

  useEffect(() => {
    checkScrollable();

    const list = objectListRef.current;
    if (list) {
      list.addEventListener("scroll", checkScrollPosition);
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(list);

      return () => {
        list.removeEventListener("scroll", checkScrollPosition);
        resizeObserver.disconnect();
      };
    }
  }, [selectedCategory, searchTerm, checkScrollable, checkScrollPosition]);

  return (
    <>
      {hostProbe}
      <Button
        className="objects-dropdown-btn"
        onClick={toggleModal}
        tooltip={
          sidebarViewMode === "grid" ? "Abrir seletor de objetos" : undefined
        }
        tooltipOptions={{ position: "right" }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarViewMode === "grid" ? "center" : "flex-start",
          width: "100%",
          height: `${BTN_HEIGHT}px`,
          minWidth: "auto",
          padding: sidebarViewMode === "grid" ? "1px" : "1px 6px",
          gap: sidebarViewMode === "list" ? "6px" : "0",
        }}
        data-evo-id="objects-dropdown-btn-settings"
      >
        <i className="pi pi-cog" style={{ fontSize: `${TOOL_ICON_SIZE}px` }} />
        {sidebarViewMode === "list" && (
          <span
            style={{
              fontSize: "12px",
              whiteSpace: "nowrap",
              color: "var(--text-color)",
              textAlign: "left",
            }}
          >
            Seletor de Objetos
          </span>
        )}
      </Button>

      <Dialog
        header={
          <div className="objects-dialog-header-inline">
            <span className="objects-dialog-title">Seletor de Objetos</span>
            <div className="objects-header-search">
              <i className="pi pi-search" />
              <InputText
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="objects-search-input"
                data-evo-id="objects-dropdown-input-search"
              />
              {searchTerm && (
                <button
                  className="objects-search-clear-icon"
                  onClick={() => setSearchTerm("")}
                  aria-label="Limpar busca"
                  title="Limpar busca"
                  type="button"
                  data-evo-id="objects-dropdown-btn-clear-search"
                >
                  <i className="pi pi-times" style={{ fontSize: "10px" }} />
                </button>
              )}
            </div>
          </div>
        }
        visible={modalVisible}
        style={{ width: "min(800px, 92vw)" }}
        contentStyle={{ padding: 0, overflow: "hidden" }}
        onHide={() => setModalVisible(false)}
        modal
        resizable={false}
        draggable={false}
        closable
        dismissableMask={false}
        closeOnEscape={false}
        className="objects-selector-dialog"
        footer={null}
        appendTo={(hostDoc ?? document).body}
        baseZIndex={100000}
      >
        <div className="objects-modal-content">
          <div className="objects-content-container">
            <div className="objects-category-list">
              <h4>Categorias</h4>
              <ul>
                {Object.keys(interactiveRegistry).map((category) => (
                  <li
                    key={category}
                    onClick={() => handleCategoryClick(category)}
                    className={`objects-category-item ${selectedCategory === category ? "selected" : ""}`}
                    data-evo-id={`objects-dropdown-category-${category}`}
                  >
                    {interactiveRegistry[category].displayName || category}
                  </li>
                ))}
              </ul>
            </div>

            <div className="objects-objects-list">
              {selectedCategory && !searchTerm && (
                <div className="objects-indicator-dropdown">
                  <h4>{selectedCategory}</h4>
                  <ul ref={objectListRef}>
                    {Object.entries(
                      interactiveRegistry[selectedCategory].interactives,
                    ).map(([typeKey, entry]) => {
                      const key = entry.key || typeKey;
                      // Prefer explicit entry.icon from the registry; fall back to key/type
                      const iconId = entry.icon || key || typeKey;
                      return (
                        <li key={key} className="objects-indicator-item">
                          <div className="objects-item-left">
                            <Icon
                              iconId={iconId}
                              size={16}
                              className="objects-icon"
                            />
                            <span className="objects-item-text">
                              {entry.displayName || typeKey}
                            </span>
                          </div>
                          <button
                            onClick={() => handleFavoriteClick(key)}
                            className="objects-favorite-btn"
                            data-evo-id={`objects-dropdown-btn-favorite-${key}`}
                          >
                            {isFavorite(key) ? "★" : "☆"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Interactive registry: simple list to toggle drawing modes */}
              {!selectedCategory &&
                !searchTerm &&
                registeredTypes &&
                registeredTypes.length > 0 && (
                  <div className="objects-interactives">
                    <h4>Interativos</h4>
                    <ul>
                      {registeredTypes.map((type) => (
                        <li key={type} className="objects-interactive-item">
                          <button
                            onClick={() =>
                              setActiveInteractive(
                                activeInteractive === type ? null : type,
                              )
                            }
                            className={`interactive-toggle ${activeInteractive === type ? "active" : ""}`}
                            data-evo-id={`objects-dropdown-btn-interactive-${type}`}
                          >
                            {type}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Favorites (from store) */}
              {!selectedCategory &&
                !searchTerm &&
                favorites &&
                favorites.length > 0 && (
                  <div className="objects-favorites">
                    <div className="objects-section-header">
                      <h4>Favoritos</h4>
                      <div className="objects-view-toggle">
                        <button
                          onClick={() => setViewMode("grid")}
                          className={`objects-view-btn ${viewMode === "grid" ? "active" : ""}`}
                          title="Visualização em grade"
                          data-evo-id="objects-dropdown-btn-view-grid"
                        >
                          <i className="pi pi-th" />
                        </button>
                        <button
                          onClick={() => setViewMode("list")}
                          className={`objects-view-btn ${viewMode === "list" ? "active" : ""}`}
                          title="Visualização em lista"
                          data-evo-id="objects-dropdown-btn-view-list"
                        >
                          <i className="pi pi-list" />
                        </button>
                      </div>
                    </div>
                    <ul className={`objects-view-${viewMode}`}>
                      {favorites.map((favKey) => {
                        const found = allInteractives.find(
                          (i) => i.key === favKey || i.type === favKey,
                        );
                        if (!found) return null;
                        // Try to resolve icon from the registry entry if available
                        const regEntry =
                          interactiveRegistry[found.category]?.interactives?.[
                            found.type
                          ] || null;
                        const iconId =
                          regEntry?.icon || found.type || found.key;
                        return (
                          <li
                            key={favKey}
                            className="objects-indicator-item"
                            title={viewMode === "grid" ? found.displayName : ""}
                          >
                            <div className="objects-item-left">
                              <Icon
                                iconId={iconId}
                                size={viewMode === "grid" ? 24 : 16}
                                className="objects-icon"
                              />
                              <span className="objects-item-text">
                                {found.displayName}
                              </span>
                            </div>
                            <button
                              onClick={() => handleFavoriteClick(favKey)}
                              className="objects-favorite-btn"
                              title={
                                viewMode === "grid"
                                  ? "Remover dos favoritos"
                                  : ""
                              }
                              data-evo-id={`objects-dropdown-btn-favorite-${favKey}`}
                            >
                              {isFavorite(favKey) ? "★" : "☆"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

              {searchTerm && filteredObjects.length > 0 && (
                <div className="objects-indicator-dropdown">
                  <h4>Resultados da Busca</h4>
                  <ul ref={objectListRef}>
                    {filteredObjects.map((object) => {
                      const key = object.key || object.type;
                      const regEntry =
                        interactiveRegistry[object.category]?.interactives?.[
                          object.type
                        ] || null;
                      const iconId = regEntry?.icon || object.type || key;
                      return (
                        <li key={key} className="objects-indicator-item">
                          <div className="objects-item-left">
                            <Icon
                              iconId={iconId}
                              size={16}
                              className="objects-icon"
                            />
                            <span className="objects-item-text">
                              {object.displayName}
                            </span>
                          </div>
                          <button
                            onClick={() => handleFavoriteClick(key)}
                            className="objects-favorite-btn"
                            data-evo-id={`objects-dropdown-btn-favorite-${key}`}
                          >
                            {isFavorite(key) ? "★" : "☆"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {searchTerm && Object.keys(filteredObjects).length === 0 && (
                <div className="objects-no-results">
                  <p>Nenhum resultado encontrado.</p>
                </div>
              )}

              {!selectedCategory && !searchTerm && (
                <div className="objects-placeholder">
                  <p>
                    Selecione uma categoria para ver os objetos disponíveis.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ObjectsDropdown;

ObjectsDropdown.propTypes = {
  onAddFavorite: PropTypes.func,
  favorites: PropTypes.array,
  interactiveApi: PropTypes.shape({
    registeredTypes: PropTypes.array,
    activeInteractive: PropTypes.string,
    setActiveInteractive: PropTypes.func,
  }),
  sidebarViewMode: PropTypes.oneOf(["grid", "list"]),
  sidebarWidth: PropTypes.number,
};
